import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import { OrganizationNiche, TemplateError } from "schemas/dashboard";

import { Template } from "features/Influencer/ContactList/schema";
import EditNicheTemplate from "features/Influencer/VA/VASettings/EditNicheDrawer/EditNicheTemplate";
import { fetcherAuth } from "utils/api";

interface Props {
  nicheEdit: OrganizationNiche;
  setNicheEdit: Dispatch<SetStateAction<OrganizationNiche | null>>;
}

const SelfServeNicheEditDialog = ({ nicheEdit, setNicheEdit }: Props) => {
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { setOrganizationVaProfile, currentOrg } = useContext(
    OrganizationUserContext,
  );

  const [modifiedTemplates, setModifiedTemplates] = useState<Template[]>([]);
  const [templatesError, setTemplatesError] = useState<TemplateError | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);

  const saveNiche = async () => {
    if (!nicheEdit) return;
    setLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches/${nicheEdit?.id}/with-automations`,
        "PUT",
        {},
        {
          templateIds: modifiedTemplates.map((template) => template.id),
        },
        true,
      );
      const jsonMessage = await res.json();

      if (!res.ok && jsonMessage.field) {
        if (
          jsonMessage.field === "templates" &&
          jsonMessage.templateIdx !== undefined &&
          jsonMessage.templateIdx !== null
        ) {
          setTemplatesError({
            idx: jsonMessage.templateIdx,
            message: jsonMessage.message,
          });
        }
        return;
      }

      setAlert(
        `Successfully updated templates for ${nicheEdit?.name}`,
        "success",
      );
      setOrganizationVaProfile((prevProfile) => {
        if (!prevProfile || !prevProfile?.niches) return prevProfile;
        const updatedNiches = prevProfile.niches.map((niche) => {
          if (niche.id === nicheEdit.id) {
            return {
              ...niche,
              templates: modifiedTemplates,
            };
          }
          return niche;
        });
        return {
          ...prevProfile,
          niches: updatedNiches,
        };
      });

      setNicheEdit(null);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={Boolean(nicheEdit?.id)}
      onClose={() => setNicheEdit(null)}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ fontSize: 26 }}>
        {`Edit Templates for ${nicheEdit?.name}`}
      </DialogTitle>
      <DialogContent>
        <EditNicheTemplate
          modifiedTemplates={modifiedTemplates}
          setModifiedTemplates={setModifiedTemplates}
          nicheTemplates={nicheEdit?.templates || []}
          templatesError={templatesError}
        />
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={() => setNicheEdit(null)}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          sx={{ px: 2 }}
          disableElevation
          onClick={saveNiche}
          loading={loading}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default SelfServeNicheEditDialog;
